import { atom, computed } from "nanostores";

import {
  createConversation,
  getConversation,
  getSessionList,
} from "../api/sdk.gen.ts";
import { CopilotMessage } from "../api/types.gen.ts";

export interface ConversationSession {
  createdAt: string;
  id: string;
  userId: string;
}

export interface ConversationState {
  currentSessionId: null | string;
  error: null | string;
  isLoading: boolean;
  messages: CopilotMessage[];
  session: ConversationSession | null;
}

// Main conversation store
export const $conversation = atom<ConversationState>({
  currentSessionId: null,
  error: null,
  isLoading: false,
  messages: [],
  session: null,
});

// Computed values
export const $isConversationLoading = computed(
  $conversation,
  (state) => state.isLoading,
);
export const $currentSessionId = computed(
  $conversation,
  (state) => state.currentSessionId,
);
export const $conversationMessages = computed(
  $conversation,
  (state) => state.messages,
);
export const $currentSession = computed(
  $conversation,
  (state) => state.session,
);
export const $conversationError = computed(
  $conversation,
  (state) => state.error,
);

// Actions
export const createNewConversation = async (): Promise<void> => {
  try {
    const currentState = $conversation.get();
    $conversation.set({ ...currentState, isLoading: true });

    const response = await createConversation();

    if (response.data) {
      $conversation.set({
        currentSessionId: response.data.id,
        error: null,
        isLoading: false,
        messages: [],
        session: response.data,
      });
    }
  } catch (error) {
    console.error("Failed to create conversation:", error);
    const currentState = $conversation.get();
    $conversation.set({
      ...currentState,
      error:
        typeof error === "object" && error !== null && "message" in error
          ? String((error as { message?: unknown }).message)
          : "Failed to create conversation. Please try again later.",
      isLoading: false,
    });
  }
};

/**
 * Ensure a conversation exists for the given session id (e.g. graphId).
 * Tries to load it first; if it doesn't exist, creates one with that id.
 */
export const ensureConversationForSession = async (
  sessionId: string,
): Promise<void> => {
  const currentState = $conversation.get();
  if (currentState.currentSessionId === sessionId) {
    return;
  }

  $conversation.set({ ...currentState, isLoading: true });

  try {
    const getResponse = await getConversation({
      body: { sessionId },
    });

    if (getResponse.data) {
      $conversation.set({
        currentSessionId: sessionId,
        error: null,
        isLoading: false,
        messages: getResponse.data.messages,
        session: getResponse.data.session,
      });
      return;
    }
  } catch {
    // Conversation not found or get failed; will try to create
  }

  try {
    const createResponse = await createConversation({
      query: { sessionId },
    });

    if (createResponse.data) {
      $conversation.set({
        currentSessionId: createResponse.data.id,
        error: null,
        isLoading: false,
        messages: [],
        session: createResponse.data,
      });
    }
  } catch (error) {
    console.error(
      "Failed to create conversation for session:",
      sessionId,
      error,
    );
    const state = $conversation.get();
    $conversation.set({
      ...state,
      error:
        typeof error === "object" && error !== null && "message" in error
          ? String((error as { message?: unknown }).message)
          : "Failed to create conversation.",
      isLoading: false,
    });
  }
};

export const loadConversation = async (sessionId: string): Promise<void> => {
  try {
    const currentState = $conversation.get();
    $conversation.set({ ...currentState, isLoading: true });

    const response = await getConversation({
      body: { sessionId },
    });

    if (response.data) {
      $conversation.set({
        currentSessionId: sessionId,
        error: null,
        isLoading: false,
        messages: response.data.messages,
        session: response.data.session,
      });
    }
  } catch (error) {
    console.error(
      `Failed to load conversation for session ID ${sessionId}:`,
      error,
    );
    const currentState = $conversation.get();
    $conversation.set({
      ...currentState,
      error: "Failed to load conversation. Please try again later.",
      isLoading: false,
    });
  }
};

export const clearConversation = (): void => {
  $conversation.set({
    currentSessionId: null,
    error: null,
    isLoading: false,
    messages: [],
    session: null,
  });
};

export const retryCreateConversation = async (): Promise<void> => {
  // Clear the error and try again
  const currentState = $conversation.get();
  $conversation.set({ ...currentState, error: null });
  await createNewConversation();
};

export const restoreLatestSession = async (): Promise<void> => {
  try {
    const currentState = $conversation.get();

    // Don't restore if we already have a session
    if (currentState.currentSessionId) {
      return;
    }

    $conversation.set({ ...currentState, isLoading: true });

    const response = await getSessionList();

    if (response.data && response.data.length > 0) {
      // Sort sessions by createdAt date (newest first) and get the first one
      const sortedSessions = response.data.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      const latestSession = sortedSessions[0];

      // Load the latest conversation
      await loadConversation(latestSession.id);
    } else {
      // No sessions found, create a new conversation
      await createNewConversation();
    }
  } catch (error) {
    console.log(
      "Could not restore latest session, creating new conversation:",
      error,
    );
    await createNewConversation();
  }
};
